import ScienceIcon from '@mui/icons-material/Science'
import {Box, IconButton, ListItem, Tooltip} from '@mui/material'
import type {FC, ReactElement, ReactNode} from 'react'
import {NavLink, useLocation} from 'react-router-dom'
import OverflowTypography from '@shared/components/OverflowTypography'

interface DrawerListItemProps {
  text: {
    link: string
    icon: ReactNode
    title: string | Element
    extraActivePaths?: string[]
  }
  index?: number
  open: boolean
  idProp: string
  badge?: ReactNode
}

const DrawerListItem: FC<DrawerListItemProps> = ({
  text,
  index,
  open,
  idProp,
  badge,
}): ReactElement => {
  const location = useLocation()

  const currentPathname = location.pathname
  const searchParams = new URLSearchParams(location.search)

  const relevantParams = ['sub_menu', 'tab']

  const textUrl = new URL(text.link, window.location.origin)
  const textPathname = textUrl.pathname
  const textParams = new URLSearchParams(textUrl.search)

  const isActive = ((): boolean => {
    const currentQueryParams = Object.fromEntries(
      [...searchParams].filter(([key]) => relevantParams.includes(key)),
    )
    const textQueryParams = Object.fromEntries(
      [...textParams].filter(([key]) => relevantParams.includes(key)),
    )
    if (text.extraActivePaths?.some((path) => currentPathname.startsWith(path))) {
      return true
    }
    if (textPathname === '/settings' && Object.keys(textQueryParams).length === 0) {
      return currentPathname.startsWith('/settings')
    }
    if (textPathname === '/incidents') {
      return currentPathname.startsWith('/incidents')
    }
    return (
      currentPathname === textPathname &&
      JSON.stringify(currentQueryParams) === JSON.stringify(textQueryParams)
    )
  })()

  return (
    <NavLink
      to={text.link as string}
      key={index}
      id="line-overview-main-link"
      data-cy="drawer-list-item-link"
    >
      {open ? (
        <ListItem
          data-testid="mbai-main-drawer-list-item"
          disablePadding
          sx={{
            display: 'block',
            pl: 1.4,
            py: 0.5,
            '& .MuiBox-root': {
              mr: 1.4,
              borderRadius: '25px',
              '&:hover': {backgroundColor: 'divider'},
            },
            '& .MuiBox-root.active': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '& .MuiSvgIcon-root': {color: 'primary.contrastText'},
              '& .MuiTypography-root': {color: 'primary.contrastText'},
            },
          }}
          id={idProp}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
            className={isActive ? 'active' : ''}
          >
            <IconButton size="large">{text.icon}</IconButton>
            <OverflowTypography
              variant="body2"
              sx={{
                display: open ? 'block' : 'none',
                ml: 0.5,
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              }}
            >
              {text.title as string}
            </OverflowTypography>
            {badge && open && (
              <Box
                sx={{
                  flexShrink: 0,
                  ml: 0.5,
                  mr: 1,
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': {backgroundColor: 'transparent !important'},
                }}
              >
                {badge}
              </Box>
            )}
          </Box>
        </ListItem>
      ) : (
        <Tooltip title={text.title as string} placement="right">
          <ListItem
            data-testid="mbai-main-drawer-list-item"
            disablePadding
            sx={{
              display: 'block',
              pl: 1.4,
              py: 0.5,
              '& .MuiBox-root': {
                mr: 1.4,
                borderRadius: '25px',
                '&:hover': {backgroundColor: 'divider'},
              },
              '& .MuiBox-root.active': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '& .MuiSvgIcon-root': {color: 'primary.contrastText'},
                '& .MuiTypography-root': {color: 'primary.contrastText'},
              },
            }}
            id={idProp}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
              className={isActive ? 'active' : ''}
            >
              <IconButton size="large" sx={{position: 'relative'}}>
                {text.icon}
                {badge && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -2,
                      right: -18,
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      backgroundColor: 'info.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <ScienceIcon sx={{fontSize: '0.9rem', color: 'white'}} />
                  </Box>
                )}
              </IconButton>
              <OverflowTypography
                variant="body2"
                sx={{
                  display: open ? 'block' : 'none',
                  ml: 0.5,
                  color: 'text.secondary',
                }}
              >
                {text.title as string}
              </OverflowTypography>
            </Box>
          </ListItem>
        </Tooltip>
      )}
    </NavLink>
  )
}

export default DrawerListItem
